// Találkozók (communication, BELSŐ nézet) — FULLCALENDAR naptár (hónap / hét / nap / lista). Az adat
// TARTOMÁNY-alapon töltődik (a látható nézet `from`/`to`-ja → a `/meetings` BFF-query), a naptár
// Europe/Budapest zónában, hétfő-kezdettel, helyszín-színkóddal jeleníti meg az eseményeket (a legacy naptár
// PONTOSAN ezt a stacket használta). Interakció: eseményre kattintás → EDIT-sheet (MeetingFormSheet a helyes
// MeetingRow-val), üres idősáv → CREATE előtöltött kezdéssel, ÁTÜTEMEZÉS drag & drop / átméretezés →
// MEGERŐSÍTÉS után PATCH (a legacy folyamat 1:1; elutasításnál/hibánál a naptár visszaáll).
//
// Jogok: page-gate `meetings.view` (VIEW-ONLY user is LÁTJA a naptárat), create/edit `meetings.manage`,
// törlés `meetings.delete` (a szerkesztő-sheet láb-gombja → megerősítés). Az INTERNAL RLS Odin-oldalon (az
// ops-token read:all → see-all). A cím-kereső (`q`) a naptár fölött opcionális, szerver-oldali szűrő.
import { type MeetingRowSchema } from '@atlas/contracts';
import { MEETING_LOCATION_KEYS, type MeetingLocationKey } from '@atlas/shared';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { MeetingFormSheet } from '@/components/meetings/meeting-form-sheet';
import { MeetingParticipantConflictList } from '@/components/meetings/meeting-participant-conflict-list';
import { MeetingPeekCard, type PeekAnchorRect } from '@/components/meetings/meeting-peek-card';
import {
  type MeetingSeriesScope,
  MeetingSeriesScopeOptions,
} from '@/components/meetings/meeting-series-scope-options';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { useTheme } from '@/components/theme-provider';
import { meetingKeys } from '@/features/meetings/api';
import { slotStartToFormStart } from '@/features/meetings/business-hours';
import {
  busyBackgroundEvents,
  canModifyMeeting,
  rowsToCalendarEvents,
} from '@/features/meetings/calendar-map';
import { MeetingsCalendar } from '@/features/meetings/components/meetings-calendar';
import {
  employeesToCheck,
  fetchParticipantConflicts,
  type MeetingParticipantConflict,
} from '@/features/meetings/participant-conflicts';
import { isRemovedByScope } from '@/features/meetings/recurrence';
import { useDeepLinkSheet } from '@/hooks/use-deep-link-sheet';
import { formatListParam, parseListParam, useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { DEFAULT_TIME_ZONE, formatDateTime } from '@/lib/format-datetime';
import { toPersonOptions } from '@/lib/person-options';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

// Deep-link: `?open=<id>` → a szerkesztő-sheet a megadott találkozóra nyílik (a command palette „2. rungja").
// FIGYELEM: a naptár TARTOMÁNY-alapon tölt (a látható nézet from/to-ja) → a deep-link CSAK akkor nyílik, ha a
// találkozó a betöltött (alapból az aktuális havi) tartományban van; egyébként graceful no-op (nincs by-id lekérés).
const meetingsSearchSchema = z.object({
  open: z.string().optional(),
  q: z.string().optional(),
  /** Helyszín-szűrő (multi, vesszős lista; üres = mind) — a `useListFilters` kulcsa, linkelhető nézet. */
  location: z.string().optional(),
  /**
   * KOLLÉGA-szűrő (multi, vesszős identity-id lista; üres = mind): csak azok a megbeszélések, ahol a kiválasztottak
   * bármelyike RÉSZTVEVŐ (nem lemondott) — a szervező nem számít. Linkelhető nézet, mint a helyszín.
   */
  participant: z.string().optional(),
  /**
   * Deep-link a LÉTREHOZÁSHOZ (`?new=1`): a kezdőlap „Gyors műveletek" kártyája ide navigál. Enélkül a
   * create-sheet csak a lap-belső gombbal nyílt volna, tehát a kezdőlapról nem lett volna elérhető
   * (a create-akciók onnan kerültek ki — user-döntés).
   */
  new: z.string().optional(),
  /**
   * Deep-link a megbeszélés NAPJÁRA (`YYYY-MM-DD`, a kezdőlap „Aktuális megbeszélések" sorából az `open` mellé):
   * a naptár erre a napra indul, így a másik hónapra eső megbeszélés is betöltődik és megnyílik. Hibás alak →
   * figyelmen kívül (a lap nem eshet a router hibalapjára egy rossz linktől).
   */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/meetings')({
  validateSearch: (search) => meetingsSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: MeetingsRoute,
});

// Route-wrapper: a deep-link search-paramot itt olvassuk + a param-törlést itt navigáljuk (a lista-oldal router-hook-mentes).
function MeetingsRoute(): JSX.Element {
  const { open, new: newParam, date } = Route.useSearch();
  const navigate = useNavigate();
  const clearDeepLink = useCallback(() => {
    void navigate({
      to: '/meetings',
      search: (prev) => ({ ...prev, open: undefined, new: undefined, date: undefined }),
      replace: true,
    });
  }, [navigate]);
  return (
    <MeetingsPage
      deepLinkOpenId={open}
      deepLinkNew={newParam === '1'}
      deepLinkDate={date}
      onClearDeepLink={clearDeepLink}
    />
  );
}

type MeetingRow = z.infer<typeof MeetingRowSchema>;

/** A deep-linkből nyitott részlet-kártya horgonya: nincs kattintott esemény, ezért a látható terület felső-közepe. */
function viewportCenterAnchor(): PeekAnchorRect {
  return { top: window.innerHeight / 4, left: window.innerWidth / 2, width: 0, height: 0 };
}
type ListPage = Awaited<ReturnType<typeof api.meetings.query>>;

// A látható tartományra egyszerre töltünk (nem lapozunk) — a puffer + a max 200 találkozó/nézet bőven elég egy
// hónapnyi belső naptárhoz; ha valaha kevés lenne, a `rangeToIso` puffert vagy a limitet emeljük.
const RANGE_LIMIT = 200;

export interface MeetingsPageProps {
  /** Deep-link: a `?open=` paraméterből érkező találkozó-id (a szerkesztő-sheet erre nyílik, ha a sor a tartományban van). */
  deepLinkOpenId?: string | undefined;
  /** Deep-link: `?new=1` → azonnal a LÉTREHOZÓ sheet nyílik (a kezdőlap gyors-műveletéből). */
  deepLinkNew?: boolean | undefined;
  /** Deep-link: `?date=YYYY-MM-DD` → a naptár erre a napra indul (az `open` megbeszélés napja). */
  deepLinkDate?: string | undefined;
  /** A deep-link param törlése (a sheet záráskor hívjuk). */
  onClearDeepLink?: () => void;
}

export function MeetingsPage({
  deepLinkOpenId,
  deepLinkNew,
  deepLinkDate,
  onClearDeepLink,
}: MeetingsPageProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('meetings.view');
  /**
   * A FELOLDOTT téma (az esemény-színek light/dark palettájához). A `theme` lehet `'system'` is — a provider a
   * NYERS beállítást adja —, ezért rendszer-módban a `matchMedia` dönt, és a rendszer-téma VÁLTOZÁSÁRA is
   * reagálunk (a nyers `theme === 'dark'` sötét rendszeren `'system'` mellett tévedne).
   */
  const { theme } = useTheme();
  const [isDark, setIsDark] = useState<boolean>(() =>
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : theme === 'dark',
  );
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const resolve = (): void => setIsDark(theme === 'system' ? mql.matches : theme === 'dark');
    resolve();
    if (theme !== 'system') return undefined;
    mql.addEventListener('change', resolve);
    return () => mql.removeEventListener('change', resolve);
  }, [theme]);
  // Jog-vezérelt akciók: meetings.manage → „+ Új" + eseményre/slotra kattintás create/edit; meetings.delete → törlés.
  const canManage = can('meetings.manage');
  const canDelete = can('meetings.delete');
  // MÁS megbeszélésének módosítása KÜLÖN JOG (a legacy `modify_any_meeting` párja): enélkül csak a SAJÁT
  // sorom szerkeszthető/törölhető/áthúzható. A fal a BFF-en van (403), ez itt a felület-oldali kényelem —
  // ne ajánljunk fel olyan műveletet, amit a szerver elutasít.
  const canModifyAny = can('meetings.modify_any');
  // A bejelentkezett identity (token `sub`): a „sajátom-e" jelölés, a halványítás és a saját RSVP forrása.
  const sessionQ = api.session.useQuery(['session'], {});
  const currentUserId = sessionQ.data?.status === 200 ? sessionQ.data.body.sub : null;

  // A szerkesztő-sheet: `null` → zárt; `{ meeting: null }` → LÉTREHOZÁS (initialStart-tal előtölthető);
  // `{ meeting }` → SZERKESZTÉS.
  const [formTarget, setFormTarget] = useState<{
    meeting: MeetingRow | null;
    initialStart?: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MeetingRow | null>(null);
  // sorozat-alkalom törlésének hatóköre (csak ez / ez és a következők / mind)
  const [deleteScope, setDeleteScope] = useState<MeetingSeriesScope>('this');
  // A RÉSZLET-KÁRTYA („peek"): eseményre kattintva nyílik, a kattintott elem téglalapjához horgonyozva.
  const [peek, setPeek] = useState<{ meeting: MeetingRow; anchor: PeekAnchorRect } | null>(null);
  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    location: '',
    participant: '',
  });
  const q = urlFilters.q;
  // A HELYSZÍN-szűrő (multi): vesszős lista az URL-ben. A `useMemo` azért kell, mert a tömb a lekérdezés-kulcs
  // része — új referencia minden renderben újrakérést jelentene.
  // Kézzel írt ismeretlen érték csendben kiesik (a BFF 400-zal utasítaná el, és a naptár hibára futna).
  const locations = useMemo(
    () =>
      parseListParam(urlFilters.location).filter((v): v is MeetingLocationKey =>
        (MEETING_LOCATION_KEYS as readonly string[]).includes(v),
      ),
    [urlFilters.location],
  );
  // A KOLLÉGA-szűrő (multi): identity-id lista az URL-ben — a szűrést a SZERVER végzi (a lapozás előtt).
  const participants = useMemo(
    () => parseListParam(urlFilters.participant),
    [urlFilters.participant],
  );
  // A választható kollégák: a szerkesztő résztvevő-választójának forrása (UGYANAZ a cache-kulcs). A BFF a gépi
  // belső fiókokat (pl. a break-glass) a munkaviszony-szabály szerint már kiszűrte.
  const refsQ = api.meetingRefs.useQuery(
    ['meeting-refs'],
    {},
    { queryKey: ['meeting-refs'], enabled: !permsLoading && canView },
  );
  const refsBody = refsQ.data?.status === 200 ? refsQ.data.body : null;
  // ⚠️ a `useMemo` load-bearing: a választó új tömb-identitásra újramountolna (l. a szerkesztő megjegyzését)
  const colleagueOptions = useMemo(
    () =>
      refsBody
        ? toPersonOptions(refsBody.employees, i18n.language).map((p) => ({
            value: p.id,
            label: p.name,
          }))
        : [],
    [refsBody, i18n.language],
  );

  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);
  // A naptár látható tartománya (UTC ISO from/to) — az `onRangeChange` állítja (az első `onRangeUpdate`-kor).
  // Amíg `null`, nem kérdezzük a szervert (a naptár még nem közölte a nézet-határokat).
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: meetings.view nélkül meg sem kérdezzük a szervert (a BFF is 403-at adna) → azonnali jog-hiba.
  const gated = !permsLoading && !canView;

  // A query-kulcs a MEGLÉVŐ meetingKeys.list — a from/to a tartomány ISO-ja, az order a naptárhoz fixen 'asc'.
  const filters = {
    q,
    location: urlFilters.location,
    participant: urlFilters.participant,
    from: range?.from ?? '',
    to: range?.to ?? '',
    order: 'asc' as const,
  };
  const listKey = meetingKeys.list(filters);

  // Tartományonként töltünk (nem infinite) — a látható nézet minden találkozóját egy kérésben kérjük le.
  const query = useQuery({
    queryKey: listKey,
    enabled: !gated && range !== null,
    queryFn: () =>
      api.meetings.query({
        query: {
          q: q || undefined,
          // a szerver szűr (a tartomány-plafon fölötti, le sem kért sorokat egy kliens-szűrés kihagyná)
          location: locations.length > 0 ? locations : undefined,
          participant: participants.length > 0 ? participants : undefined,
          from: range?.from,
          to: range?.to,
          order: 'asc',
          limit: RANGE_LIMIT,
        },
      }),
  });

  const okBody = query.data?.status === 200 ? query.data.body : undefined;
  const rows = useMemo(() => okBody?.meetings ?? [], [okBody]);
  // A BFF jelzi, ha a lista a védő-plafon miatt CSONKA — a naptár ilyenkor NEM a teljes tartományt mutatja,
  // tehát ezt ki kell írni (egy hiányzó meeting a naptárban észrevétlen marad).
  const truncated = okBody?.truncated ?? false;
  // Az esemény-színek TÉMA-függők (light/dark paletta) → a feloldott témát adjuk a leképezésnek.
  // KOLLÉGA-szűrésnél a FOGLALTSÁGI RÉTEG (a kiválasztottak megbeszéléseinek összevont ideje, sávozott háttérként
  // a hét/nap nézetben) — a fehéren maradt rész a közös szabad idő. Szűrő nélkül nincs réteg.
  const events = useMemo(
    () => [
      ...rowsToCalendarEvents(rows, isDark ? 'dark' : 'light', currentUserId, canModifyAny),
      ...(participants.length > 0 ? busyBackgroundEvents(rows) : []),
    ],
    [rows, isDark, currentUserId, canModifyAny, participants.length],
  );

  // Deep-link: a `?open=<id>` találkozót nyitjuk meg, ha a sor a betöltött tartományban van (különben graceful
  // no-op — a naptár tartomány-alapon tölt, nincs by-id lekérés; a `?date` ezért a megbeszélés napjára állítja).
  // Aki SZERKESZTHETI (jog + tulajdon), annak a szerkesztő nyílik; aki nem, annak a részlet-kártya — a szerkesztő
  // mentése neki 403-ba futna. A döntéshez a saját identity kell, ezért a session betöltéséig nem nyitunk.
  useDeepLinkSheet<MeetingRow>({
    id: deepLinkOpenId,
    rows: sessionQ.isLoading ? [] : rows,
    getId: (m) => m.id,
    onOpen: (m) => {
      if (canManage && canModifyMeeting(m, currentUserId, canModifyAny))
        setFormTarget({ meeting: m });
      else setPeek({ meeting: m, anchor: viewportCenterAnchor() });
    },
  });

  // Deep-link a LÉTREHOZÁSHOZ (`?new=1` — a kezdőlap gyors-műveletéből): egyszer nyitjuk a create-sheetet, és
  // a paramot a záráskor a wrapper törli. Jog nélkül no-op (a sheet mentése amúgy is 403-ba futna).
  const newSheetOpened = useRef(false);
  useEffect(() => {
    if (deepLinkNew !== true || newSheetOpened.current || !can('meetings.manage')) return;
    newSheetOpened.current = true;
    setFormTarget({ meeting: null });
  }, [deepLinkNew, can]);
  // A kártya a FRISS sorra mutat: RSVP/átütemezés után a lista invalidálódik, és a kártya tartalma követi
  // (id-alapú újrakeresés — enélkül a régi snapshot maradt volna nyitva).
  const peekMeeting = peek === null ? null : (rows.find((r) => r.id === peek.meeting.id) ?? null);
  const isForbidden = gated || isForbiddenStatus(query.data?.status);
  const loading = !gated && (permsLoading || (range !== null && query.isLoading));

  // A látható tartomány változása (nézet-/dátum-váltás) → új fetch a tartományra.
  const onRangeChange = useCallback((from: string, to: string) => setRange({ from, to }), []);
  // Eseményre kattintás → RÉSZLET-KÁRTYA (nem azonnal a szerkesztő): időpont/helyszín/szervező +
  // résztvevők + saját RSVP, és onnan egy explicit „Szerkesztés" gomb. Ez a naptár-minta (Google/Outlook):
  // a kattintás elsődlegesen MEGNÉZ, nem módosít — a résztvevők így minden nézetből egy kattintásra látszanak.
  const onEventClick = useCallback(
    (id: string, anchor: PeekAnchorRect) => {
      const m = rows.find((r) => r.id === id);
      if (m) setPeek({ meeting: m, anchor });
    },
    [rows],
  );
  // Üres nap/idősáv kattintás → CREATE-sheet előtöltött kezdéssel (csak manage-joggal). A hónap-nézet
  // NAP-cellája 00:00-t ad — oda a munkaidő-kezdés kerül (nem hajnalra rögzítünk találkozót).
  const onSelectSlot = useCallback(
    (localStart: string, allDay: boolean) => {
      if (canManage) {
        setFormTarget({ meeting: null, initialStart: slotStartToFormStart(localStart, allDay) });
      }
    },
    [canManage],
  );

  // A naptár locale-ja a nyelvhez kötött (a FullCalendar a `locale` prop változását követi — nem kell remount).
  const calendarLocale = i18n.language.startsWith('hu') ? 'hu' : 'en';

  /**
   * ÁTÜTEMEZÉS (drag & drop / átméretezés) — a legacy folyamat 1:1: a naptár nem ment azonnal, hanem
   * MEGERŐSÍTÉST kér, és csak utána ír. Elutasításnál VAGY szerver-hibánál a naptár `revert()`-je visszateszi
   * az eseményt a régi helyére (különben a felület hazudna: elmozdult esemény mentés nélkül).
   *
   * A mentés a MEGLÉVŐ `meetingUpdate` végponton megy (If-Match/OCC), a sor összes mezőjével + az ÚJ
   * időpontokkal — így a BFF ütközés-ellenőrzése (helyszín-átfedés · Zoom-fiók) az átütemezésre IS lefut, és
   * az értesítés (`meeting.rescheduled`) is a szokásos úton indul.
   *
   * RÉSZTVEVŐ-ÜTKÖZÉS: a megerősítő dialógus ELŐTT lekérjük, ki ül már egy átfedő megbeszélésen — ha van ilyen,
   * UGYANAZ a dialógus sárga tónussal sorolja fel őket (nem külön lépés; figyelmeztetés, nem tiltás).
   */
  const [rescheduleTarget, setRescheduleTarget] = useState<{
    meeting: MeetingRow;
    startsAt: string;
    endsAt: string;
    revert: () => void;
    conflicts: MeetingParticipantConflict[];
  } | null>(null);

  // VERSENY-ŐR: az ütközés-lekérés alatt (a naptár az eseményt már áthelyezte) egy újabb húzás nem írhatja felül
  // a függőt — azt azonnal visszaállítjuk, különben az első esemény mentés nélkül maradna az új helyén
  const rescheduleBusyRef = useRef(false);
  // az őr a megerősítő dialógus lezárultáig (mentés / mégse / hiba) él
  useEffect(() => {
    if (rescheduleTarget === null) rescheduleBusyRef.current = false;
  }, [rescheduleTarget]);
  const onReschedule = useCallback(
    (req: { id: string; startsAt: string; endsAt: string; revert: () => void }) => {
      if (rescheduleBusyRef.current) {
        req.revert();
        return;
      }
      const meeting = rows.find((r) => r.id === req.id);
      if (!meeting) {
        req.revert();
        return;
      }
      // TULAJDON-FAL (defense-in-depth): a nem-sajátom eseményt a naptár már nem is engedi megfogni
      // (esemény-szintű `editable`), de ha bármi mégis idejut, itt megállunk — a szerver 403-at adna.
      if (!canModifyMeeting(meeting, currentUserId, canModifyAny)) {
        toast.error(t('toast:meetings.notOwn'));
        req.revert();
        return;
      }
      // A LEGACY ELŐ-ELLENŐRZÉSE: ha az új VÉG már elmúlt, meg sem kérdezzük — azonnali, érthető
      // visszajelzés + visszaállás. (A szerver-oldali fal ettől függetlenül megvan; ez az UX-fél: enélkül a
      // felhasználó egy mező-hibás 422-t kapna egy vonszolásra.)
      if (new Date(req.endsAt).getTime() < Date.now()) {
        toast.error(t('toast:meetings.reschedulePast'));
        req.revert();
        return;
      }
      const internal = meeting.participants.filter((p) => p.participantRole === 'internal');
      const toCheck = employeesToCheck(
        { startsAt: req.startsAt, endsAt: req.endsAt, employeeIds: internal.map((p) => p.id) },
        {
          startsAt: meeting.startsAt,
          endsAt: meeting.endsAt,
          employeeIds: internal.map((p) => p.id),
          declinedIds: internal.filter((p) => p.status === 'declined').map((p) => p.id),
        },
      );
      rescheduleBusyRef.current = true;
      void fetchParticipantConflicts({
        startsAt: req.startsAt,
        endsAt: req.endsAt,
        employeeIds: toCheck,
        excludeId: meeting.id,
      }).then((conflicts) =>
        setRescheduleTarget({
          meeting,
          startsAt: req.startsAt,
          endsAt: req.endsAt,
          revert: req.revert,
          conflicts,
        }),
      );
    },
    [rows],
  );

  const rescheduleMutation = useEntityMutation<{
    meeting: MeetingRow;
    startsAt: string;
    endsAt: string;
  }>({
    mutationFn: ({ meeting, startsAt, endsAt }) =>
      api.meetingUpdate.mutation({
        params: { id: meeting.id },
        headers: { 'if-match': String(meeting.version) },
        body: {
          title: meeting.title,
          description: meeting.description,
          locationKey: meeting.locationKey ?? 'other',
          startsAt,
          endsAt,
          zoomAccountKey: meeting.zoomAccountKey,
          zoomLink: meeting.zoomLink,
          googleMeetLink: meeting.googleMeetLink,
          microsoftTeamsLink: meeting.microsoftTeamsLink,
          // A résztvevő-halmaz VÁLTOZATLAN: a sorból küldjük vissza (a BFF diffel — így nem esik ki senki).
          employeeIds: meeting.participants
            .filter((p) => p.participantRole === 'internal')
            .map((p) => p.id),
          contactIds: meeting.participants
            .filter((p) => p.participantRole === 'partner')
            .map((p) => p.id),
        },
      }),
    invalidateKeys: [meetingKeys.lists()],
    messages: {
      success: t('toast:meetings.rescheduled'),
      conflict: t('toast:meetings.conflict'),
      forbidden: t('toast:meetings.forbidden'),
      validation: t('toast:meetings.validation'),
      error: t('toast:meetings.error'),
    },
  });

  function confirmReschedule(): void {
    const target = rescheduleTarget;
    if (!target) return;
    rescheduleMutation.mutate(
      { meeting: target.meeting, startsAt: target.startsAt, endsAt: target.endsAt },
      {
        // HIBA (ütközés/jog/validáció) → a naptár visszaáll: a felület sosem mutat nem-mentett állapotot.
        onError: () => target.revert(),
        onSettled: () => setRescheduleTarget(null),
      },
    );
  }

  // Törlés (optimista sor-eltávolítás a látható tartomány cache-éből + rollback): a soft-delete a BFF
  // `/meetings/:id` (meetings.delete). A szerkesztő-sheet láb-gombja → megerősítés → ez a mutáció.
  const removeMutation = useEntityMutation<{ id: string; scope: MeetingSeriesScope }, ListPage>({
    mutationFn: ({ id, scope }) => api.meetingDelete.mutation({ params: { id }, query: { scope } }),
    invalidateKeys: [meetingKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        if (old?.status !== 200) return old;
        if (!deleteTarget) return old;
        const removed = (m: MeetingRow): boolean =>
          isRemovedByScope(m, deleteTarget, deleteScope, Date.now());
        const kept = old.body.meetings.filter((m) => !removed(m));
        return {
          ...old,
          body: {
            ...old.body,
            meetings: kept,
            total: Math.max(0, old.body.total - (old.body.meetings.length - kept.length)),
          },
        };
      },
    },
    messages: {
      success: t('toast:meetings.deleted'),
      conflict: t('toast:meetings.conflict'),
      forbidden: t('toast:meetings.forbidden'),
      validation: t('toast:meetings.validation'),
      error: t('toast:meetings.error'),
    },
  });

  function confirmDelete(): void {
    if (!deleteTarget) return;
    const scope = deleteTarget.seriesId ? deleteScope : 'this';
    removeMutation.mutate(
      { id: deleteTarget.id, scope },
      { onSettled: () => setDeleteTarget(null) },
    );
  }

  return (
    <AppShell
      // Mobilon a lebegő FAB (primaryAction) kezeli a létrehozást — csak ha van manage-jog. Desktopon a toolbar
      // ListNewButton-ja (lásd lent, `hidden md:block`).
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('meetings.new'),
              onClick: () => setFormTarget({ meeting: null }),
            }
          : undefined
      }
      toolbar={
        // KÖZÖS ListToolbar: cím-kereső + darabszám-meta (a látható tartományban) + „+ Új". A dátum-tartományt a
        // naptár-navigáció adja (nincs külön dátum-szűrő), így a sáv tiszta.
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          filterItems={[
            {
              kind: 'multi',
              key: 'location',
              label: t('meetings.filterLocation'),
              // a helyszín-nevek TULAJDONNEVEK („Tárgyaló", „Keszthelyi iroda") — a szűrőben is úgy írjuk, mint a
              // jelvényen és a szerkesztőben (a szűrő-opciók kisbetűsítése tulajdonnévre nem vonatkozik)
              preserveCase: true,
              values: locations,
              onChange: (v) => setFilters({ location: formatListParam(v) }),
              options: MEETING_LOCATION_KEYS.map((v) => ({
                value: v,
                label: t(`meetings.locationLabel.${v}`),
              })),
            },
            {
              kind: 'multi',
              key: 'participant',
              label: t('meetings.filterParticipant'),
              // személynevek — tulajdonnév, nem kisbetűsítjük
              preserveCase: true,
              values: participants,
              onChange: (v) => setFilters({ participant: formatListParam(v) }),
              options: colleagueOptions,
            },
          ]}
          meta={
            loading
              ? t('meetings.loading')
              : !isForbidden
                ? t('meetings.countInRange', { count: rows.length }) +
                  (participants.length > 0 ? ` · ${t('meetings.busyLegend')}` : '') +
                  (truncated ? ` · ${t('common.listTruncated')}` : '')
                : ''
          }
          actions={
            // A New gomb CSAK ≥md — mobilon a lebegő FAB (primaryAction) kezeli a létrehozást.
            <div className="hidden md:block">
              <ListNewButton
                label={t('meetings.new')}
                onClick={() => setFormTarget({ meeting: null })}
                enabled={canManage}
                disabledTooltip={t('common.noCreatePermission')}
              />
            </div>
          }
        />
      }
    >
      {isForbidden ? (
        <div className="px-4 pb-2">
          <ListErrorState forbidden onRetry={() => void query.refetch()} />
        </div>
      ) : (
        <div className="flex h-full flex-col px-4 pb-4">
          <MeetingsCalendar
            initialDate={deepLinkDate}
            locale={calendarLocale}
            events={events}
            onRangeChange={onRangeChange}
            onEventClick={onEventClick}
            onSelectSlot={canManage ? onSelectSlot : undefined}
            // ÁTÜTEMEZÉS csak `meetings.manage` joggal — enélkül a naptár nem is draggelhető.
            onReschedule={canManage ? onReschedule : undefined}
            weekendToggleLabels={{
              label: t('meetings.weekendLabel'),
              hide: t('meetings.hideWeekends'),
              show: t('meetings.showWeekends'),
            }}
            buttonLabels={{
              today: t('meetings.calendarButtons.today'),
              month: t('meetings.calendarButtons.month'),
              week: t('meetings.calendarButtons.week'),
              day: t('meetings.calendarButtons.day'),
            }}
          />
        </div>
      )}

      {/* RÉSZLET-KÁRTYA: a naptár-eseményre kattintva. A saját identity a session-lekérésből (a kártya
          RSVP-vezérlője ehhez kötött); a „Szerkesztés" a kártyát zárja és a sheetet nyitja. */}
      <MeetingPeekCard
        meeting={peekMeeting}
        anchor={peek?.anchor ?? null}
        open={peekMeeting !== null}
        onOpenChange={(o) => {
          if (!o) {
            setPeek(null);
            onClearDeepLink?.(); // a deep-linkből nyitott kártya zárásakor a `?open` is törlődik
          }
        }}
        currentUserId={currentUserId}
        canManage={canManage}
        canModifyAny={canModifyAny}
        onEdit={(m) => {
          setPeek(null);
          setFormTarget({ meeting: m });
        }}
      />

      <MeetingFormSheet
        open={formTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setFormTarget(null);
            onClearDeepLink?.(); // deep-link: záráskor a `?open` param törlése (back-barát + újra-nyitható)
          }
        }}
        meeting={formTarget?.meeting ?? null}
        initialStart={formTarget?.initialStart}
        // A TÖRLÉS a `meetings.delete` jog MELLETT tulajdon-kérdés is: más sorát csak `meetings.modify_any`
        // joggal lehet törölni (a BFF is ezt érvényesíti) — a láb-gomb ezért feltételes.
        canDelete={
          canDelete &&
          (formTarget?.meeting == null ||
            canModifyMeeting(formTarget.meeting, currentUserId, canModifyAny))
        }
        // A törlés-kérés bezárja a szerkesztőt és megnyitja a megerősítést (nincs egymásra torlódó modal).
        onRequestDelete={(m) => {
          setFormTarget(null);
          onClearDeepLink?.();
          setDeleteScope('this');
          setDeleteTarget(m);
        }}
      />

      {/* ÁTÜTEMEZÉS megerősítése (a legacy confirmation-modal párja) — az új időpont a szövegben. */}
      <ConfirmDialog
        open={rescheduleTarget !== null}
        onOpenChange={(open) => {
          if (!open && rescheduleTarget) {
            // Bezárás mentés NÉLKÜL → a naptár visszaáll (az esemény a régi helyére kerül).
            rescheduleTarget.revert();
            setRescheduleTarget(null);
          }
        }}
        title={t('meetings.rescheduleTitle')}
        description={t('meetings.rescheduleDesc', {
          title: rescheduleTarget?.meeting.title ?? '',
          start: rescheduleTarget
            ? `${formatDateTime(rescheduleTarget.startsAt, i18n.language, DEFAULT_TIME_ZONE, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })} – ${formatDateTime(rescheduleTarget.endsAt, i18n.language, DEFAULT_TIME_ZONE, {
                timeStyle: 'short',
              })}`
            : '',
        })}
        confirmLabel={t('meetings.rescheduleConfirm')}
        cancelLabel={t('meetings.cancel')}
        pendingLabel={t('meetings.saving')}
        pending={rescheduleMutation.isPending}
        onConfirm={confirmReschedule}
        tone={rescheduleTarget && rescheduleTarget.conflicts.length > 0 ? 'warning' : 'default'}
      >
        {rescheduleTarget?.meeting.seriesId && (
          <p className="text-muted-foreground mt-3 text-sm">
            {t('meetings.seriesScope.rescheduleOnlyThis')}
          </p>
        )}
        {rescheduleTarget && rescheduleTarget.conflicts.length > 0 && (
          <>
            <p className="text-muted-foreground mt-3 text-sm">
              {t('meetings.participantConflict.desc', {
                count: rescheduleTarget.conflicts.length,
              })}
            </p>
            <MeetingParticipantConflictList conflicts={rescheduleTarget.conflicts} />
          </>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('meetings.deleteTitle')}
        description={t('meetings.deleteDesc', { title: deleteTarget?.title ?? '' })}
        confirmLabel={t('meetings.delete')}
        cancelLabel={t('meetings.cancel')}
        pendingLabel={t('meetings.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      >
        {deleteTarget?.seriesId && (
          <MeetingSeriesScopeOptions
            value={deleteScope}
            onChange={setDeleteScope}
            action="delete"
            disabled={removeMutation.isPending}
          />
        )}
      </ConfirmDialog>
    </AppShell>
  );
}
